import React, { useState, useEffect } from 'react';
import DashboardTab from '../components/admin/DashboardTab';
import CronJobsTab from '../components/admin/CronJobsTab';
import ActiveUsersTab from '../components/admin/ActiveUsersTab';
import RegistrationsTab from '../components/admin/RegistrationsTab';
import MemberSearchTab from '../components/admin/MemberSearchTab';
import MatchesTab from '../components/admin/MatchesTab';
import CommunitiesTab from '../components/admin/CommunitiesTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import LeaderboardTab from '../components/admin/LeaderboardTab';
import ScriptLogsTab from '../components/admin/ScriptLogsTab';
import AiMatcherTab from '../components/admin/AiMatcherTab';
import { api } from '../api/client';
import { getStoredUser, mergeStoredUser, getUsername } from '../auth/userStorage';
import './Admin2Page.css';

const Admin2Page = ({ user: propUser }) => {
    const [activeTab, setActiveTab] = useState('members');
    const [authorized, setAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);

    // Use prop from App.js or fallback to localStorage
    const user = propUser || getStoredUser();

    useEffect(() => {
        if (!user) { setLoading(false); return; }

        // If status already known from session — use it
        if (user.status === 'Admin') {
            setAuthorized(true);
            setLoading(false);
            return;
        }

        // Status missing from session (clobbered localStorage) — check via API
        const username = getUsername(user);
        if (!username) { setLoading(false); return; }

        api.get(`/api/admin/members/search?q=${encodeURIComponent(username)}`, { auth: 'admin', user })
            .then(data => {
                if (data.success && data.members?.length > 0) {
                    const me = data.members.find(m => m.tg_username === username);
                    if (me?.status === 'Admin') {
                        setAuthorized(true);
                        // Fix localStorage for next time
                        mergeStoredUser({ status: 'Admin' });
                    }
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [user]);

    if (loading) {
        return <div className="admin2-loading">Loading...</div>;
    }

    if (!authorized) {
        return (
            <div className="admin2-unauthorized">
                <h2>Access Denied</h2>
                <p>Admin access required.</p>
                <a href="/">Back to App</a>
            </div>
        );
    }

    const tabs = [
        { id: 'members', label: 'Members' },
        { id: 'matches', label: 'Matches' },
        { id: 'communities', label: 'Communities' },
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'cron', label: 'Cron Jobs' },
        { id: 'logs', label: 'Script Logs' },
        { id: 'matcher', label: 'AI Matcher' },
        { id: 'active', label: 'Active Users' },
        { id: 'registrations', label: 'Registrations' },
        { id: 'analytics', label: 'Analytics' },
        { id: 'leaderboard', label: 'Leaderboard' },
    ];

    const handleLogout = () => {
        localStorage.removeItem('user');
        window.location.href = '/';
    };

    return (
        <div className="admin2-page">
            <header className="admin2-header">
                <div className="admin2-header-left">
                    <h1 className="admin2-title">Linked.Coffee Admin</h1>
                </div>
                <div className="admin2-header-right">
                    <a href="/" className="admin2-btn admin2-btn-secondary">Back to App</a>
                    <button onClick={handleLogout} className="admin2-btn admin2-btn-secondary">Logout</button>
                </div>
            </header>

            <nav className="admin2-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`admin2-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </nav>

            <main className="admin2-content">
                {activeTab === 'members' && <MemberSearchTab user={user} />}
                {activeTab === 'matches' && <MatchesTab user={user} />}
                {activeTab === 'communities' && <CommunitiesTab user={user} />}
                {activeTab === 'dashboard' && <DashboardTab user={user} />}
                {activeTab === 'cron' && <CronJobsTab user={user} />}
                {activeTab === 'logs' && <ScriptLogsTab user={user} />}
                {activeTab === 'matcher' && <AiMatcherTab user={user} />}
                {activeTab === 'active' && <ActiveUsersTab user={user} />}
                {activeTab === 'registrations' && <RegistrationsTab user={user} />}
                {activeTab === 'analytics' && <AnalyticsTab user={user} />}
                {activeTab === 'leaderboard' && <LeaderboardTab user={user} />}
            </main>
        </div>
    );
};

export default Admin2Page;
